import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Logger } from '@core/services';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from '@ngneat/dialog';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { PaginatedEntity, Pagination, UserEntity } from '@core/entities';
import { EnumToKeyValueArray, isMobileView, sortData } from '@core/utils';
import { UseClub } from '@core/usecases';
import { FormBuilder } from '@angular/forms';
import { CurrentSort, FilterQuery } from '@core/types';
import { DropDownItem } from '@core/types/Dropdown.type';
import { ROLE } from '@auth';

const log = new Logger('Club Member Composition');

@UntilDestroy()
export class ClubMembersComposition {
  users: UserEntity[];
  sortedUsers: UserEntity[];
  isLoading = true;
  pagination: Pagination = new PaginatedEntity().pagination;
  currentSort: CurrentSort = { column: '', direction: '' };

  filters: FilterQuery = {
    search: '',
    roles: '',
    status: '',
  };

  roleFilterItems: DropDownItem[];
  statusFilterItems: DropDownItem[];

  protected readonly _translateService = inject(TranslateService);
  protected readonly _dialogService = inject(DialogService);
  protected readonly _bottomSheetService = inject(NgxBottomSheetService);
  private readonly _useClub = new UseClub();
  private readonly _fb: FormBuilder = inject(FormBuilder);

  constructor() {
    this.roleFilterItems = EnumToKeyValueArray(ROLE, this._translateService, false);
    this.statusFilterItems = [
      { key: this._translateService.instant('All'), value: '' },
      { key: 'Online', value: 'online' },
      { key: 'Offline', value: 'offline' },
    ];
  }

  onSortChange({ column, direction }: CurrentSort) {
    this.currentSort = { column, direction };
    this.sortedUsers = sortData(this.users, column as keyof (typeof this.users)[0], direction);
  }

  onSearch() {
    this.pagination.page = 1;
    this.fetchAllClubMembers();
  }

  onFilterChange(value: any, type: 'roles' | 'status') {
    this.filters[type] = value;
    if (!isMobileView()) {
      this.pagination.page = 1;
      this.fetchAllClubMembers();
    }
  }

  protected paginationChange(pagination: Pagination) {
    this.pagination = pagination;
    this.fetchAllClubMembers();
  }

  protected fetchAllClubMembers() {
    this.isLoading = true;

    const filterQuery = { ...this.filters };
    let value = filterQuery['roles'];
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
      value = value.map((item) => item.value).join(',');
    } else if (Array.isArray(value)) {
      value = value.join(',');
    }
    filterQuery['roles'] = value;

    this._useClub
      .getAllMembers(this.pagination, filterQuery)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          if (res) {
            this.users = res.members;
            this.sortedUsers = this.users;
            this.pagination = res.pagination;
            this.isLoading = false;
            if (isMobileView()) {
              window.scrollTo(0, 0);
            }
          } else {
            log.info('No Members found');
            this.isLoading = false;
          }
        },
        error: (error) => {
          log.error('Error while fetching Members', error);
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }
}
